<template>
  <div v-if="categoryTree">
    <div class="current-question">
      VRAAG {{ orderPointer + 1 }}/{{ questionOrder.length }}
    </div>
    <transition name="slide-fade" mode="out-in">
      <div v-if="!switching && currentCategory" class="question-header">
        <Question
          :header="`Wat voor ${currentCategory?.name} zoekt u?`"
          :subtext="currentCategory?.description"
        />
      </div>
      <content-placeholders centered v-else>
        <content-placeholders-heading
          centered
          style="max-width: 200px; margin: 1rem auto"
        />
        <content-placeholders-text
          :lines="2"
          style="max-width: 700px; margin: 1rem auto"
        />
      </content-placeholders>
    </transition>

    <div class="action-buttons my-4">
      <button class="btn btn-border" @click="handlePrev">Prev</button>
      <button class="btn btn-primary" @click="handleNext">
        {{ hasPreference ? "Next" : "No Preference" }}
        <font-awesome-icon class="ml-2" icon="fa-solid fa-arrow-right" />
      </button>
    </div>

    <transition name="fade" mode="out-in">
      <div key="1" class="loader" v-if="switching || !currentCategory"></div>

      <div key="2" v-else-if="currentCategory?.subcategories" class="row">
        <div
          v-for="subcat in currentCategory.subcategories"
          :key="subcat.id"
          style="padding: 10px"
          class="col-md-6"
        >
          <CategoryItem
            :category="subcat"
            :isSelected="isSelected(subcat.id)"
            :disabled="subcat.count < 1"
            :noProducts="subcat.count < 1"
            :autoDisable="orderPointer > 0"
            :filteredProducts="filteredProducts"
            @click="handleSelectCategory(subcat)"
            @auto-deselect="(e) => handleDeselectCategory(e)"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import Question from "./Question.vue";
import CategoryItem from "./CategoryItem.vue";
import ProductCard from "../ProductCard.vue";
import { getProducts } from "@/helpers/getAllCategoryProducts";
import { groupCategoryIds } from "@/helpers/categoryTree";

export default {
  name: "WizzardSepper",
  components: {
    Question,
    CategoryItem,
    ProductCard,
  },
  props: {
    questionOrder: {
      type: Array,
    },
    categoryTree: {
      type: Array,
    },
  },
  emits: ["finish"],
  data() {
    return {
      orderPointer: 0,
      selectedCategories: [],
      currentParentCategory: null,
      fetchProducts: false,
      products: [],
      switching: false,
      filteredProducts: [],
    };
  },
  methods: {
    async handleNext() {
      this.switching = true;

      if (this.orderPointer === this.questionOrder.length - 1) {
        const formattedCategories = this.selectedCategories
          .map((cat) => [cat.data.id])
          .flat(1);
        return this.$emit("finish", formattedCategories);
      }

      await this.handleGetCategoryProducts();

      if (this.orderPointer < this.questionOrder.length - 1) {
        this.orderPointer += 1;
      }

      this.setFilteredProducts();

      setTimeout(() => (this.switching = false), 300);
    },

    handlePrev() {
      this.switching = true;
      if (this.orderPointer > 0) {
        this.orderPointer -= 1;
      }
      this.setFilteredProducts();
      setTimeout(() => (this.switching = false), 500);
    },

    isSelected(subcategoryId) {
      return (
        this.selectedCategories.findIndex(
          (cat) => cat.data.id === subcategoryId
        ) >= 0
      );
    },

    handleDeselectCategory(subcategory) {
      this.selectedCategories = this.selectedCategories.filter(
        (cat) => cat.data.id !== subcategory.id
      );
    },

    handleSelectCategory(subcategory) {
      const selectionIndex = this.selectedCategories.findIndex(
        (cat) => cat.data.id === subcategory.id
      );

      if (selectionIndex > -1) {
        this.handleDeselectCategory(subcategory);
        return;
      }

      if (this.selectedCategories[selectionIndex]?.data.id === subcategory.id) {
        return this.selectedCategories.splice(selectionIndex, 1);
      }

      if (selectionIndex < 0) {
        this.selectedCategories.push({
          data: subcategory,
          orderPointer: this.orderPointer,
        });
      } else {
        this.selectedCategories[selectionIndex].data = subcategory;
        const half = Math.ceil(this.selectedCategories.length / 2);
        this.selectedCategories.splice(selectionIndex + 1, half);
      }
    },

    async handleGetCategoryProducts() {
      if (!this.selectedCategories.length) {
        const data = await getProducts();
        this.products = [...data];
        return;
      }

      try {
        const categoryCounts = this.selectedCategories.map(
          (cat) => cat.data.count
        );
        const data = await getProducts({
          categoryId: this.selectedCategories
            .map((cat) => cat.data.id)
            .join(","),
          categoryProductCount: categoryCounts.reduce((a, b) => a + b, 0),
          productsPerPage: 99,
        });

        if (this.orderPointer === 0) {
          this.products = [...data];
        } else {
          this.filteredProducts = [...data];
        }
      } catch (e) {
        // eslint-disable-next-line
        console.log(e);
      }
    },

    setFilteredProducts() {
      const categoryList = this.selectedCategories
        .filter((cat) => cat.orderPointer < this.orderPointer)
        .map((cat) => cat.data.id);

      const groupedIds = groupCategoryIds(this.categoryTree, categoryList);

      this.filteredProducts = this.products.filter((product) => {
        const productCategorIds = product.categories.map(
          (category) => category.id
        );

        const results = Object.keys(groupedIds)
          .filter((key) => key !== "0")
          .map((key) => {
            return productCategorIds.filter((catId) => {
              return groupedIds[key].includes(catId);
            }).length
              ? true
              : false;
          });

        return results.every((result) => result === true);
      });
    },
  },
  computed: {
    currentIndex() {
      return this.questionOrder[this.orderPointer];
    },

    currentCategory() {
      const categ = this.categoryTree?.find(
        (cat) => cat.categoryIndex === this.currentIndex
      );

      if (!this.filteredProducts?.length) {
        categ?.subcategories.sort((a, b) => b.count - a.count);
        return categ;
      }

      categ.subcategories = categ?.subcategories
        .map((category) => {
          const productsInCategory = this.filteredProducts.filter((product) => {
            const prod = product.categories.find((prodCat) => {
              return category.id === prodCat.id;
            });

            return prod ? true : false;
          });

          return {
            ...category,
            autoDisable: productsInCategory.length ? false : true,
          };
        })
        .sort((a, b) => b.count - a.count)
        .sort((a, b) => a.autoDisable - b.autoDisable);

      return categ;
    },

    hasPreference() {
      const selectionIndex = this.selectedCategories.find(
        (cat) => cat.orderPointer === this.orderPointer
      );

      return selectionIndex ? true : false;
    },
  },
};
</script>

<style scoped>
.question-header {
  text-align: center;
}

.current-question {
  text-align: center;
  margin-bottom: 3rem;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 1rem;
}
</style>
